/**
 * @file A custom React hook for managing the state and validation of a message input field.
 */

import { useState, useMemo, useCallback } from 'react';

/**
 * Defines the possible contexts for the message.
 */
export type ContextType = 'family' | 'work' | 'friends' | 'dating';

/**
 * Validation error codes for i18n translation.
 * Use with t(`message_input.validation.${errorCode}`, { min, max }) for translation.
 */
export type ValidationErrorCode = 'empty' | 'too_short' | 'too_long';

/**
 * Validation error with code and interpolation values for i18n.
 */
export interface ValidationError {
  /** Error code for translation lookup: message_input.validation.{code} */
  code: ValidationErrorCode;
  /** Interpolation values for the translation string */
  values?: { min?: number; max?: number };
}

/**
 * Options for configuring the useMessageInput hook.
 */
export interface MessageInputOptions {
  /** Maximum character limit (varies by subscription tier). Defaults to 500. */
  maxLength?: number;
}

/**
 * Defines the shape of the state and methods returned by the useMessageInput hook.
 */
export interface MessageInputState {
  /** The current text content of the message. */
  message: string;
  /** The selected context for the message. */
  context: ContextType;
  /** The current character count of the message. */
  characterCount: number;
  /** The maximum character limit based on subscription tier. */
  maxLength: number;
  /**
   * The validation error, or null if there is no error.
   * Use with t(`message_input.validation.${error.code}`, error.values) for translation.
   */
  error: ValidationError | null;
  /** A boolean indicating if the message is currently valid (between MIN_LENGTH and maxLength chars). */
  isValid: boolean;
  /** Function to update the message text. */
  setMessage: (text: string) => void;
  /** Function to update the message context. */
  setContext: (context: ContextType) => void;
  /** Function to clear the message text and reset validation. */
  clearMessage: () => void;
  /**
   * Triggers validation and updates the error state.
   * @returns {boolean} - True if the message is valid, otherwise false.
   */
  validateMessage: () => boolean;
}

const MIN_LENGTH = 10;
const DEFAULT_MAX_LENGTH = 500;

/**
 * A custom hook to manage the state and validation for a message input.
 *
 * @param options - Configuration options for the hook
 * @param options.maxLength - Maximum character limit (defaults to 500, varies by subscription tier)
 * @returns {MessageInputState} - The state and methods for managing the message input.
 */
export function useMessageInput(options: MessageInputOptions = {}): MessageInputState {
  const maxLength = options.maxLength ?? DEFAULT_MAX_LENGTH;

  const [message, setMessageState] = useState('');
  const [context, setContextState] = useState<ContextType>('family');
  const [error, setError] = useState<ValidationError | null>(null);

  const characterCount = useMemo(() => message.length, [message]);

  // Derived state that reflects raw validity, useful for disabling UI elements.
  const isValid = useMemo(() => {
    const trimmedLength = message.trim().length;
    return trimmedLength >= MIN_LENGTH && trimmedLength <= maxLength;
  }, [message, maxLength]);

  const setMessage = useCallback((text: string) => {
    setMessageState(text);
    // Clear previous validation error when user starts typing again.
    if (error !== null) {
      setError(null);
    }
  }, [error]);

  const setContext = useCallback((newContext: ContextType) => {
    setContextState(newContext);
  }, []);

  const clearMessage = useCallback(() => {
    setMessageState('');
    setError(null);
  }, []);

  const validateMessage = useCallback((): boolean => {
    const trimmed = message.trim();

    if (trimmed.length === 0) {
      setError({ code: 'empty' });
      return false;
    }
    if (trimmed.length < MIN_LENGTH) {
      setError({ code: 'too_short', values: { min: MIN_LENGTH } });
      return false;
    }
    if (trimmed.length > maxLength) {
      setError({ code: 'too_long', values: { max: maxLength } });
      return false;
    }

    setError(null);
    return true;
  }, [message, maxLength]);

  return {
    message,
    context,
    characterCount,
    maxLength,
    error,
    isValid,
    setMessage,
    setContext,
    clearMessage,
    validateMessage,
  };
}
